import { FormattedNumberInputField } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TEditPermanentServiceACPModalSchema, EditPermanentServiceACPModalSchema } from "../Models";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import { TFunction } from "i18next";
import { useParams } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import {  PermanentServiceFragmentDoc, ServicePermission, OrganizationQuery, OrganizationQueryVariables, OrganizationDocument } from "@/graphql/__generated__/graphql";
import { convertBpToDecimal, convertDecimalToBp, getNestedError } from "@/utils/helpers";
import { ExclamationTriangleIcon, GearIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { Switch } from "@/components/ui/switch";
import { useEditPermanentServiceMutationHook } from "@/hooks";
import { useFragment } from "@apollo/client/react";
import { useQuery } from "@apollo/client/react";
import { IntlShape, useIntl } from "react-intl";
import ACPDefaultComponent from "./ACPDefaultComponent";
import { useEffect, useState } from "react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import CircleLoader from "@/components/shared/loaders/CircleLoader";
import { cn } from "@/lib/utils";
import { AverageLengthOfEmploymentTooltip, TransferredEmployeePercentageTooltip } from "@/components/shared";

interface IFormInput {
  name: string;
  label: string;
  isInteger?: boolean;
  additionalSuffix?: string;
  placeholder?: string;
  value?: number;
  isDifferent?: boolean;
  style?: "currency" | "decimal";
  tooltip?: string;
  spaceBetweenSuffix?: boolean;
  customTooltip?: React.ReactNode;
  tooltipTriggerClassName?: string;
}

const createBaseFormInputs = (t: TFunction): IFormInput[] => {
  return [
    {
      name: "hourlyWage",
      label: t("permanentservice.inputs.hourlywage.label"),
      placeholder: t("permanentservice.inputs.hourlywage.placeholder"),
      additionalSuffix: "/h",
      style: "currency"
    },
    {
      name: "nonTariffAllowance",
      label: t("permanentservice.inputs.nontariffallowance.label"),
      placeholder: t("permanentservice.inputs.nontariffallowance.placeholder"),
      additionalSuffix: "/h",
      style: "currency"
    },
    {
      name: "hoursBriefingPerEmployee",
      label: t("permanentservice.inputs.hoursbriefingperemployee.label"),
      placeholder: t("permanentservice.inputs.hoursbriefingperemployee.placeholder"),
      isInteger: true
    },
    {
      name: "transferredEmployeePercentage",
      label: t("permanentservice.inputs.transferredemployeepercentage.label"),
      placeholder: t("permanentservice.inputs.transferredemployeepercentage.placeholder"),
      additionalSuffix: "%",
      customTooltip: <TransferredEmployeePercentageTooltip />,
      tooltipTriggerClassName: "text-orange-500"
    }
  ];
};

const createACPFormInputs = (t: TFunction, defaultValues: TEditPermanentServiceACPModalSchema, organizationValue: any): IFormInput[] => {
  return [
    {
      name: "acp.averageHoursWorkedPerMonth",
      label: t("edittender.defaults.inputs.averagehoursworkedpermonth.label"),
      placeholder: t("edittender.defaults.inputs.averagehoursworkedpermonth.placeholder"),
      isInteger: true,
      value: organizationValue?.averageHoursWorkedPerMonth,
      isDifferent: Number(defaultValues.acp.averageHoursWorkedPerMonth) !== organizationValue?.averageHoursWorkedPerMonth
    },
    {
      name: "acp.trainingDays",
      label: t("edittender.defaults.inputs.trainingdays.label"),
      placeholder: t("edittender.defaults.inputs.trainingdays.placeholder"),
      isInteger: true,
      value: organizationValue?.trainingDays,
      isDifferent: Number(defaultValues.acp.trainingDays) !== organizationValue?.trainingDays
    },
    {
      name: "acp.yearlyTrainingCosts",
      label: t("edittender.defaults.inputs.yearlytrainingcosts.label"),
      placeholder: t("edittender.defaults.inputs.yearlytrainingcosts.placeholder"),
      style: "currency",
      value: organizationValue?.yearlyTrainingCosts,
      isDifferent: Number(defaultValues.acp.yearlyTrainingCosts) !== organizationValue?.yearlyTrainingCosts
    },
    {
      name: "acp.sickDays",
      label: t("edittender.defaults.inputs.sickdays.label"),
      placeholder: t("edittender.defaults.inputs.sickdays.placeholder"),
      isInteger: true,
      value: organizationValue?.sickDays,
      isDifferent: Number(defaultValues.acp.sickDays) !== organizationValue?.sickDays
    },
    {
      name: "acp.employeeFluctuation",
      label: t("edittender.defaults.inputs.employeefluctuation.label"),
      placeholder: t("edittender.defaults.inputs.employeefluctuation.placeholder"),
      additionalSuffix: "%",
      value: convertBpToDecimal(organizationValue?.employeeFluctuation ?? 0),
      isDifferent: Number(defaultValues.acp.employeeFluctuation) !== convertBpToDecimal(organizationValue?.employeeFluctuation ?? 0)
    },
    {
      name: "acp.yearlyUniformCosts",
      label: t("edittender.defaults.inputs.yearlyuniformcosts.label"),
      placeholder: t("edittender.defaults.inputs.yearlyuniformcosts.placeholder"),
      style: "currency",
      value: organizationValue?.yearlyUniformCosts,
      isDifferent: Number(defaultValues.acp.yearlyUniformCosts) !== organizationValue?.yearlyUniformCosts
    },
    {
      name: "acp.otherMaterialCosts",
      label: t("edittender.defaults.inputs.othermaterialcosts.label"),
      placeholder: t("edittender.defaults.inputs.othermaterialcosts.placeholder"),
      style: "currency",
      value: organizationValue?.otherMaterialCosts,
      isDifferent: Number(defaultValues.acp.otherMaterialCosts) !== organizationValue?.otherMaterialCosts
    },
    {
      name: "acp.averageLengthOfEmployment",
      label: t("edittender.defaults.inputs.averagelengthofemployment.label"),
      placeholder: t("edittender.defaults.inputs.averagelengthofemployment.placeholder"),
      additionalSuffix: t("edittender.defaults.inputs.averagelengthofemployment.suffix"),
      spaceBetweenSuffix: true,
      isInteger: true,
      value: organizationValue?.averageLengthOfEmployment,
      isDifferent: Number(defaultValues.acp.averageLengthOfEmployment) !== organizationValue?.averageLengthOfEmployment,
      customTooltip: <AverageLengthOfEmploymentTooltip />,
      tooltipTriggerClassName: "text-orange-500"
    }
  ];
};

const renderInputs = (intl: IntlShape, t: TFunction, inputs: IFormInput[], userCanEdit: boolean, control: any, register: any, errors: any, editPermanentServiceLoading: boolean) => {
  return inputs.map((input) => {
    const error = getNestedError(errors, input.name);

    return (
      <div key={`defaults-form-input-${input.name}`} className="form-container">
        <div className="flex items-center gap-2">
          <Label htmlFor={input.name} className={cn("flex items-center", input.isDifferent && "text-orange-500", error && "text-red-500")}>
            {
              input.isDifferent && (
                <ExclamationTriangleIcon className="inline h-4 mr-1 text-orange-500" />
              )
            }
            {input.label}
          </Label>
          {
            input.tooltip || input.customTooltip && (
              <HoverCard openDelay={100} closeDelay={0}>
                <HoverCardTrigger asChild>
                  <Button variant="ghost" size="icon" className={cn("p-0 h-fit w-fit hover:cursor-help", input.tooltipTriggerClassName)} type="button">
                    <InfoCircledIcon className="inline h-4" />
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent align="end" className="w-fit">
                  {input.customTooltip || <p className="max-w-[320px]">{input.tooltip}</p>}
                </HoverCardContent>
              </HoverCard>
            )
          }
        </div>
        <div className="flex items-center">
          <FormattedNumberInputField
            name={input.name}
            control={control}
            disabled={!userCanEdit || editPermanentServiceLoading}
            isInteger={input.isInteger}
            additionalSuffix={input.additionalSuffix}
            placeholder={input.placeholder}
            style={input.style}
            spaceBetweenSuffix={input.spaceBetweenSuffix}
            className={cn("z-10", input.value && "rounded-r-none")}
          />
          {
            input.value && (
              <ACPDefaultComponent t={t} intl={intl} type={input.style === "currency" ? "currency" : "default"} value={input.value} label={input.label} />
            )
          }
        </div>
        {error && (
          <p className="input-error-message">
            {error?.message as string}
          </p>
        )}
      </div>
    );
  });
};

function EditPermanentServiceModal() {

  // Hooks
  const { serviceID } = useParams();
  const { t } = useTranslation(["translation", "common", "modals"]);
  const intl = useIntl();

  const [isOpen, setIsOpen] = useState(false);

  const { editPermanentServiceMutation, loading: editPermanentServiceLoading } = useEditPermanentServiceMutationHook();

  const serviceData = useFragment({
    fragment: PermanentServiceFragmentDoc,
    from: {
      id: serviceID,
      __typename: "PermanentService"
    }
  });
  const service = serviceData.data;

  const { data: organizationData, loading: organizationLoading, refetch } = useQuery<OrganizationQuery, OrganizationQueryVariables>(OrganizationDocument, {
    variables: {
      organizationId: service?.operationalOrganizationId ?? 0
    },
    skip: !service?.operationalOrganizationId
  });
  const organization = organizationData?.organization;

  const userCanEdit = service?.permissions?.includes(ServicePermission.Edit) ?? false;

  const defaultValues = {
    name: service.name ?? "",
    info: service.info ?? "",
    acp: {
      averageHoursWorkedPerMonth: service.advancedCostParameters?.averageHoursWorkedPerMonth ?? 0,
      trainingDays: service.advancedCostParameters?.trainingDays ?? 0,
      yearlyTrainingCosts: service.advancedCostParameters?.yearlyTrainingCosts ?? 0,
      sickDays: service.advancedCostParameters?.sickDays ?? 0,
      employeeFluctuation: convertBpToDecimal(service.advancedCostParameters?.employeeFluctuation ?? 0),
      yearlyUniformCosts: service.advancedCostParameters?.yearlyUniformCosts ?? 0,
      otherMaterialCosts: service.advancedCostParameters?.otherMaterialCosts ?? 0,
      averageLengthOfEmployment: service.advancedCostParameters?.averageLengthOfEmployment ?? 0,
      carCostsPerMinute: service.advancedCostParameters?.carCostsPerMinute ?? 0,
      otherCostsPerMinute: service.advancedCostParameters?.otherCostsPerMinute ?? 0,
    },
    hourlyWage: service.hourlyWage ?? 0,
    hoursBriefingPerEmployee: service.hoursBriefingPerEmployee ?? 0,
    transferredEmployeePercentage: (service.transferredEmployeePercentage ?? 0) / 100,
    nonTariffAllowance: service.nonTariffAllowance ?? 0,
    pauseIncluded: service.pauseIncluded ?? false,
  };

  const baseFormInputs = createBaseFormInputs(t);
  const ACPFormInputs = createACPFormInputs(t, defaultValues, organization?.advancedCostParameters);

  const containDifferences = ACPFormInputs.some((input) => input.isDifferent);

  const { control, register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<TEditPermanentServiceACPModalSchema>({
    resolver: zodResolver(EditPermanentServiceACPModalSchema) as Resolver<TEditPermanentServiceACPModalSchema>,
    defaultValues,
    mode: "onChange"
  });

  const onInvalid = (errors: unknown) => console.error(errors);

  const pauseIncluded = watch("pauseIncluded");

  /**
   * OnSubmit handler for the form
   * @param {TEditPermanentServiceACPModalSchema} data
   * @returns
   */
  const onSubmit: SubmitHandler<TEditPermanentServiceACPModalSchema> = (data: TEditPermanentServiceACPModalSchema) => {
    // update employeeFluctuation value to basis point
    const adjustedData = {
      ...data,
      nonTariffAllowance: data.nonTariffAllowance ?? 0,
      acp: {
        ...data.acp,
        employeeFluctuation: data.acp.employeeFluctuation * 100,
      },
      transferredEmployeePercentage: convertDecimalToBp(data.transferredEmployeePercentage),
    };

    editPermanentServiceMutation({
      variables: {
        serviceId: Number(serviceID),
        input: adjustedData,
      }
    }).then(() => {
      setIsOpen(false);
    });
  };

  const handleResetForm = () => {
    reset(defaultValues);
  };

  useEffect(() => {
    if (serviceData && !organization) {
      refetch();
    }
  }, [serviceData]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" onClick={handleResetForm} className={cn((!organizationLoading && containDifferences) && "text-orange-500 border-orange-500")}>
          {
            !organizationLoading && containDifferences ? (
              <ExclamationTriangleIcon className="inline h-4 mr-1" />
            ) : (
              <GearIcon className="h-4 w-4 mr-1" />
            )
          }
          {t("modals:editpermanentservicemodal.trigger")}
        </Button>
      </DialogTrigger>
      <DialogContent className="md:min-w-fit overflow-y-auto max-w-[90vw] lg:max-w-fit h-full md:h-auto px-0">
        <form onSubmit={handleSubmit(onSubmit, onInvalid)} id="defaults-form" className="flex-col-4">
          <DialogHeader className="px-4">
            <DialogTitle>{t("modals:editpermanentservicemodal.title")}</DialogTitle>
            <DialogDescription>
              {t("modals:editpermanentservicemodal.description")}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[calc(90vh-10rem)] pr-2">
            <div className="flex-col-4 pl-4 pr-2 pb-4">
              <div>
                <h3 className="text-sm text-slate-400">{t("modals:editpermanentservicemodal.general")}</h3>
                <Separator />
              </div>
              <div className="flex flex-col gap-2">
                <div>
                  <Label htmlFor="name" className={cn("mb-2", getNestedError(errors, "name") && "text-red-500")}>
                    {t("modals:editpermanentservicemodal.inputs.name.label")}
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder={t("modals:editpermanentservicemodal.inputs.name.placeholder")}
                    disabled={!userCanEdit || editPermanentServiceLoading}
                    {...register("name")}
                    className={cn(getNestedError(errors, "name") && "border-red-500")}
                  />
                  {getNestedError(errors, "name") && (
                    <p className="input-error-message">
                      {getNestedError(errors, "name")?.message as string}
                    </p>
                  )}
                </div>
                <div className="mt-2">
                  <Label htmlFor="info" className={cn("mb-2", getNestedError(errors, "info") && "text-red-500")}>
                    {t("modals:editpermanentservicemodal.inputs.info.label")}
                    <span className="text-slate-400 font-normal">&nbsp;({t("optional")})</span>
                  </Label>
                  <Textarea
                    id="info"
                    placeholder={t("modals:editpermanentservicemodal.inputs.info.placeholder")}
                    disabled={!userCanEdit || editPermanentServiceLoading}
                    {...register("info")}
                    className={cn("max-h-[200px] resize-none", getNestedError(errors, "info") && "border-red-500")}
                  />
                  {getNestedError(errors, "info") && (
                    <p className="input-error-message">
                      {getNestedError(errors, "info")?.message as string}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm text-slate-400">{t("modals:editpermanentservicemodal.base")}</h3>
                <Separator />
              </div>
              <div className="flex-wrap-4">
                {renderInputs(intl, t, baseFormInputs, userCanEdit, control, register, errors, editPermanentServiceLoading)}
                <div className="w-full md:min-w-[48%] md:flex-1 space-y-1">
                  <div className="flex gap-2 items-center space-y-0.5">
                    <Label>
                      {t("permanentservice.inputs.pauseincluded.label")}
                    </Label>
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button variant="ghost" size="icon" className="p-0 h-fit w-fit hover:cursor-help" type="button">
                          <InfoCircledIcon className="inline h-4" />
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent>
                        <p className="max-w-[320px]">{t("permanentservice.inputs.pauseincluded.note")}</p>
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                  <Switch
                    id="pauseIncluded"
                    {...register("pauseIncluded")}
                    disabled={!userCanEdit || editPermanentServiceLoading}
                    checked={pauseIncluded}
                    onCheckedChange={(value) => {
                      setValue("pauseIncluded", value);
                    }}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-sm text-slate-400">{t("modals:editpermanentservicemodal.advancedcostparameters")}</h3>
                <Separator />
              </div>

              {
                containDifferences && (
                  <div className="flex items-center text-xs text-orange-500">
                    <ExclamationTriangleIcon className="inline h-4 mr-1" />
                    <p>{t("modals:editpermanentservicemodal.differentvalues")}</p>
                  </div>
                )
              }

              <div className="flex-wrap-4">
                {renderInputs(intl, t, ACPFormInputs, userCanEdit, control, register, errors, editPermanentServiceLoading)}
              </div>

            </div>
          </ScrollArea>
          <DialogFooter className="px-4">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={editPermanentServiceLoading}>
                {t("common:buttons.close")}
              </Button>
            </DialogClose>
            {
              userCanEdit && (
                <Button type="submit" disabled={editPermanentServiceLoading} className="gap-2">
                  <CircleLoader visible={editPermanentServiceLoading} height={16} width={16} strokeWidth={2} />
                  {t("common:buttons.save")}
                </Button>
              )
            }
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditPermanentServiceModal;
